import { inject, Injectable } from '@angular/core';
import { DEFAULT_MAX_RESULT_COUNT, ZnSelectQueryParams } from '@zendra/shared';
import { PropertyService, PropertyTypeService, VendorService } from 'cbd-proxies';
import { ContractAssociationOrchestratorService } from 'orchestrator/proxy';
import { map, Observable } from 'rxjs';

import { IContractAssociationSelectOption } from '../interfaces/IContractSelectOption';
import { IAssociateStandCycleRotationOption } from '../interfaces/IAssociateStandCycleRotationOption';
import { DEFAULT_PROPERTIES_MAX_RESULT_COUNT } from '../contract-assignment-by-stand.config';
import type {
  ContractQueryInputDto,
  GetFomentContractsInputDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/integrations/bio-forest-abp/models';
import { GetStandCycleRotationsForAssociationInputDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/contract-association/models';

@Injectable({
  providedIn: 'root',
})
export class ContractAssociation {
  private readonly contractAssociationService = inject(ContractAssociationOrchestratorService);
  private readonly vendorService = inject(VendorService);
  private readonly propertyService = inject(PropertyService);
  private readonly propertyTypeService = inject(PropertyTypeService);

  loadVendors(
      params?: ZnSelectQueryParams
    ): Observable<{ items: { key: string; value: string }[]; totalCount: number; } > {
      return this.vendorService
        .getList({
          fantasyName: params?.search,
          skipCount: params?.skipCount,
          maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
        })
        .pipe(
          map(response => ({
            items: (response.items ?? []).map(item => ({
              key: String(item.id),
              value: item.fantasyName ?? String(item.id),
            })),
            totalCount: response.totalCount ?? 0,
          })),
        );
    }

    loadContracts(
      params?: ContractQueryInputDto
    ): Observable<{ items: IContractAssociationSelectOption[]; totalCount: number; } > {
      return this.contractAssociationService
        .getContractsAssociationByInput({
          vendorId: params?.vendorId,
          contractMasterGroupCode: params?.contractMasterGroupCode,
          isActive: params?.isActive,
          skipCount: params?.skipCount,
          maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
        })
        .pipe(
          map(response => ({
            items: (response.items ?? []).map(item => ({
              key: String(item.contractId),
              value: item.number ?? String(item.contractId),
              description: item.contractTypeName
            })),
            totalCount: response.totalCount ?? 0,
          })),
        );
    }

    loadFomentContracts(
      params?: GetFomentContractsInputDto & Omit<ZnSelectQueryParams, 'ids'>,
    ): Observable<{ items: IContractAssociationSelectOption[]; totalCount: number }> {
      return this.contractAssociationService
        .getFomentContractsAssociation({
          vendorId: params?.vendorId,
          number: params?.search ?? params?.number,
          ids: params?.ids,
          skipCount: params?.skipCount,
          maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
        })
        .pipe(
          map(response => ({
            items: (response.items ?? []).map(item => ({
              key: String(item.fomentContractId),
              value: item.number ?? String(item.fomentContractId),
              description: item.contractTypeName,
            })),
            totalCount: response.totalCount ?? 0,
          })),
        );
    }

    loadProperties(
      params?: ZnSelectQueryParams
    ): Observable<{ items: { key: string; value: string }[]; totalCount: number; } > {
      return this.propertyService
        .getList({
          name: params?.search,
          skipCount: params?.skipCount,
          maxResultCount: params?.maxResultCount ?? DEFAULT_PROPERTIES_MAX_RESULT_COUNT,
        })
        .pipe(
          map(response => ({
            items: (response.items ?? []).map(item => ({
              key: String(item.id),
              value: item.name ?? String(item.id),
            })),
            totalCount: response.totalCount ?? 0,
          })),
        );
    }

    loadPropertyTypes(
      params?: ZnSelectQueryParams
    ): Observable<{ items: { key: string; value: string }[]; totalCount: number; } > {
      return this.propertyTypeService
        .getList({
          name: params?.search,
          skipCount: params?.skipCount,
          maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
        })
        .pipe(
          map(response => ({
            items: (response.items ?? []).map(item => ({
              key: String(item.id),
              value: item.name ?? String(item.id),
            })),
            totalCount: response.totalCount ?? 0,
          })),
        );
    }

    loadStandCycleRotations(
      params?: GetStandCycleRotationsForAssociationInputDto
    ): Observable<{ items: IAssociateStandCycleRotationOption[]; totalCount: number; } > {
      return this.contractAssociationService
        .getStandCycleRotations({
          propertyId: params?.propertyId,
          filter: params?.filter,
          skipCount: params?.skipCount,
          maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
        })
        .pipe(
          map(response => ({
            items: (response.items ?? []).map(item => ({
              ...item,
              key: String(item.id),
              value: `${item.standName} - ${item.cycle} - ${item.rotation}`
            })),
            totalCount: response.totalCount ?? 0,
          })),
        );
    }
}
